import annotateError from '../utils/annotate-error';
import sanitizeUrl from '../utils/sanitize-url';

interface ChunkLoadErrorDetails {
	code?: unknown;
	message?: unknown;
	name?: unknown;
	request?: unknown;
	type?: unknown;
}

interface RouteAssetFailure {
	assetUrl?: string;
	code?: string;
	name?: string;
	online?: boolean;
	serviceWorkerControlled?: boolean;
	timestampMs: number;
	type?: string;
}

interface RouteAssetRetry {
	attempts: number;
	firstFailure: RouteAssetFailure;
	lastFailure?: RouteAssetFailure;
	outcome: 'exhausted' | 'recovered';
	retryDelayMs: number;
}

const ROUTE_CHUNK_RETRY_DELAY = 350;

/**
 * Captures native asset markers and browser state without retaining raw error text.
 *
 * @param error Rejection from the original import or its retry.
 * @returns A sanitized snapshot taken before another request can replace the evidence.
 */
function captureAssetFailure(error: unknown): RouteAssetFailure {
	const failure: RouteAssetFailure = {
		timestampMs: Date.now(),
	};

	try {
		if (error && typeof error === 'object') {
			const details: ChunkLoadErrorDetails = error;

			if (typeof details.request === 'string') {
				failure.assetUrl = sanitizeUrl(details.request);
			}
			if (details.code === 'CSS_CHUNK_LOAD_FAILED') {
				failure.code = details.code;
			}
			if (typeof details.name === 'string' && [
				'ChunkLoadError',
				'Error',
				'TypeError',
			].includes(details.name)) {
				failure.name = details.name;
			}
			if (typeof details.type === 'string' && [
				'error',
				'missing',
				'timeout',
			].includes(details.type)) {
				failure.type = details.type;
			}
		}
		if (typeof navigator !== 'undefined') {
			if (typeof navigator.onLine === 'boolean') {
				failure.online = navigator.onLine;
			}
			if ('serviceWorker' in navigator) {
				failure.serviceWorkerControlled = !!navigator.serviceWorker.controller;
			}
		}
	} catch {
		/** Diagnostic getters must never interrupt route recovery. */
	}

	return failure;
}

/**
 * Retries an asset failure once while retaining the first attempt's evidence.
 *
 * @param importModule Dynamic import for the route and its extracted styles.
 * @returns The imported module, or the final import rejection.
 */
async function importRouteModule<Module>(importModule: () => Promise<Module>): Promise<Module> {
	try {
		return await importModule();
	} catch (error) {
		if (!isRouteChunkLoadError(error)) {
			throw error;
		}

		const firstFailure = captureAssetFailure(error);
		await new Promise<void>((resolve) => {
			setTimeout(
				resolve,
				ROUTE_CHUNK_RETRY_DELAY,
			);
		});

		let importedModule: Module;
		try {
			importedModule = await importModule();
		} catch (retryError) {
			recordAssetRetry(
				{
					attempts: 2,
					firstFailure,
					lastFailure: captureAssetFailure(retryError),
					outcome: 'exhausted',
					retryDelayMs: ROUTE_CHUNK_RETRY_DELAY,
				},
				retryError,
			);
			throw retryError;
		}

		recordAssetRetry({
			attempts: 2,
			firstFailure,
			outcome: 'recovered',
			retryDelayMs: ROUTE_CHUNK_RETRY_DELAY,
		});
		return importedModule;
	}
}

/**
 * Adds bounded retry context without creating another exception report.
 *
 * @param retry Original failure and the outcome of its one automatic retry.
 * @param error Final rejection, when both imports failed.
 */
function recordAssetRetry(retry: RouteAssetRetry, error?: unknown): void {
	try {
		if (error !== undefined) {
			annotateError(
				error,
				'routeAssetLoad',
				{
					...retry,
				},
			);
		}
	} catch {
		/** Frozen errors must retain their original rejection identity. */
	}

	try {
		if (typeof window !== 'undefined') {
			window.glBugsnagClient?.leaveBreadcrumb(
				'route asset retry',
				{
					...retry,
				},
				'manual',
			);
		}
	} catch {
		/** Unavailable or failing telemetry must not affect an import outcome. */
	}
}

/**
 * Shares route imports across matched records and permits a fresh load after failure.
 *
 * @param importModule Dynamic import for one route module.
 * @returns A loader that shares pending and successful imports, with one asset retry.
 */
export function createRouteModuleLoader<Module>(importModule: () => Promise<Module>): () => Promise<Module> {
	let modulePromise: Promise<Module> | undefined;

	/**
	 * Starts or reuses the route import without creating an unhandled eager rejection.
	 *
	 * @returns The shared import promise, preserving its rejection for Vue Router.
	 */
	return function loadRouteModule(): Promise<Module> {
		if (!modulePromise) {
			const pendingModule: Promise<Module> = importRouteModule(importModule);
			modulePromise = pendingModule;

			/**
			 * Vue Router starts every matched import eagerly, but observes their
			 * guards one at a time. A later import may reject before its guard runs
			 * or after an earlier guard aborts. Observe shared rejections immediately
			 * to prevent duplicate unhandled reports, while returning the original
			 * promise so the active guard still fails through router.onError.
			 */
			void pendingModule.catch(() => {
				if (modulePromise === pendingModule) {
					modulePromise = undefined;
				}
			});
		}

		return modulePromise;
	};
}

/**
 * Identifies webpack JavaScript and extracted CSS asset-loading failures.
 *
 * @param error Rejected value from a route import or navigation.
 * @returns Whether the rejection describes a failed route asset request.
 */
export function isRouteChunkLoadError(error: unknown): boolean {
	if (!error || typeof error !== 'object') {
		return false;
	}

	const details: ChunkLoadErrorDetails = error;

	return details.name === 'ChunkLoadError'
		|| details.code === 'CSS_CHUNK_LOAD_FAILED'
		|| (typeof details.message === 'string' && /^Loading (?:CSS )?chunk \S+ failed\b/u.test(details.message));
}
